import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { buildWAUrl } from "@/lib/format";
import { Sparkles, X, Loader2, RefreshCw, MessageCircle } from "lucide-react";

export default function WaAiComposer({ open, onClose, client, onSent }) {
  const [goal, setGoal] = useState("");
  const [variants, setVariants] = useState([]);
  const [sel, setSel] = useState(-1);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async (g) => {
    if (!client?.id) return;
    setLoading(true); setVariants([]); setSel(-1); setText("");
    try {
      const r = await api.post("/copilot/wa-draft", { client_id: client.id, goal: g ?? goal });
      setVariants(r.data?.variants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI non disponibile, riprova");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setGoal(""); generate(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.id]);

  if (!open || !client) return null;
  const fullName = `${client.cognome || ""} ${client.nome || ""}`.trim();

  const sendWa = () => {
    const t = text.trim();
    if (!t) return;
    window.open(buildWAUrl(client.telefono, t), "_blank", "noopener,noreferrer");
    api.post(`/clients/${client.id}/updates`, { text: "📱 WhatsApp inviato (scritto con AI)" }).catch(() => {});
    toast.success("WhatsApp aperto col messaggio AI");
    onSent?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="wa-ai-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 flex items-center gap-2 shrink-0" style={{ background: "#0A192F" }}>
          <Sparkles className="w-5 h-5" style={{ color: "#D4AB6E" }} />
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-sm truncate">Scrivi con AI — {fullName}</div>
            <div className="text-[10px] text-white/50">Messaggio su misura basato sullo storico</div>
          </div>
          <button onClick={onClose} data-testid="wa-ai-close" className="p-1.5 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="flex gap-2">
            <input value={goal} onChange={(e) => setGoal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
              placeholder="Obiettivo (opzionale): es. riproporre PP, fissare chiamata…"
              data-testid="wa-ai-goal"
              className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-[#B7955B]" />
            <button onClick={() => generate()} disabled={loading} data-testid="wa-ai-regen"
              className="px-3 py-2 text-xs font-semibold border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Rigenera
            </button>
          </div>

          {loading && (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#B7955B] mx-auto" />
              <div className="text-xs text-slate-400 mt-2">L'AI sta leggendo lo storico di {client.nome || fullName}…</div>
            </div>
          )}

          {!loading && variants.map((v, i) => (
            <button key={i} onClick={() => { setSel(i); setText(v); }} data-testid={`wa-ai-variant-${i}`}
              className={`w-full text-left p-3 rounded-xl border text-[12.5px] leading-relaxed transition ${sel === i ? "border-[#B7955B] bg-[#FBF7EF] shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#8a6e3d]">Variante {i + 1}</span>
              <div className="text-slate-700 mt-0.5">{v}</div>
            </button>
          ))}

          {sel >= 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Modifica prima di inviare</div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                data-testid="wa-ai-edit"
                className="w-full px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-[#B7955B] resize-none" />
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0 bg-slate-50">
          <button onClick={onClose} className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700">Annulla</button>
          <button onClick={sendWa} disabled={!text.trim()} data-testid="wa-ai-send"
            className="px-4 py-2.5 text-xs font-bold text-white rounded-lg flex items-center gap-1.5 disabled:opacity-40"
            style={{ background: "#25D366" }}>
            <MessageCircle className="w-4 h-4" /> Apri WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}
